import { AxiosResponse } from 'axios';
import { customTeamCreate, customTeamDelete, customTeamList, customTeamUpdate, customTeamUploadLogo } from '@/api/customTeam';
import { AppError } from '@/errors/appError';
import type { CustomTeamItem } from '@/models/customTeam';
import { baseListService, isRequestCanceled } from '@/services/baseService';
import { ICustomTeamApiListResponse, ICustomTeamUpdateParams, ICustomTeamUpdateResponse, ICustomTeamUploadLogoResponse, IListParams } from '@/types';

export const customTeamService = {
    async list(params: IListParams<Record<string, any>>): Promise<AxiosResponse<ICustomTeamApiListResponse>> {
        try {
            return await baseListService<ICustomTeamApiListResponse>(customTeamList, params);
        } catch (e) {
            if (isRequestCanceled(e)) throw e;
            console.error('CustomTeam API Error:', e);
            throw new AppError(`Unable to load custom team list, request error`, { cause: e });
        }
    },

    async save(item: CustomTeamItem): Promise<AxiosResponse<ICustomTeamUpdateResponse>> {
        const row = item.toApiData();
        if (item.id) {
            return this.update({ rows: [row] });
        }
        return this.create({ rows: [row] });
    },

    async create(params: ICustomTeamUpdateParams): Promise<AxiosResponse<ICustomTeamUpdateResponse>> {
        try {
            return await customTeamCreate(params);
        } catch (e) {
            console.error('CustomTeam create API Error:', e);
            throw new AppError('Unable to create custom team, request error', { cause: e });
        }
    },

    async update(params: ICustomTeamUpdateParams): Promise<AxiosResponse<ICustomTeamUpdateResponse>> {
        try {
            return await customTeamUpdate(params);
        } catch (e) {
            console.error('CustomTeam update API Error:', e);
            throw new AppError('Unable to update custom team, request error', { cause: e });
        }
    },

    async delete(id: number): Promise<AxiosResponse<ICustomTeamUpdateResponse>> {
        try {
            return await customTeamDelete({ rows: [{ id }] });
        } catch (e) {
            console.error('CustomTeam delete API Error:', e);
            throw new AppError('Unable to delete custom team, request error', { cause: e });
        }
    },

    async uploadLogo(id: number, file: File): Promise<AxiosResponse<ICustomTeamUploadLogoResponse>> {
        try {
            return await customTeamUploadLogo(id, file);
        } catch (e) {
            console.error('CustomTeam uploadLogo API Error:', e);
            throw new AppError('Unable to upload custom team logo, request error', { cause: e });
        }
    },
};
